import { corridorRelayQuery } from "@common/api/corridor-relay"
import { erpApiKeyQuery } from "@common/api/erp-api-keys"
import { Button } from "@common/components/ui/button"
import { Card, CardContent } from "@common/components/ui/card"
import { useHasPermission } from "@common/hooks/use-permissions"
import { formatShortDate } from "@common/lib/date-utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, Outlet } from "@tanstack/react-router"
import { PageContent, PageTitle } from "@/components/page/page-content"
import { SectionHeader } from "@/components/page/section"
import { IntegrationRow } from "@/routes/_layout/settings/_panel/integrations/crm/-components/integration-row"

export const Route = createFileRoute(
  "/_layout/settings/_panel/integrations/erp/_list",
)({
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(erpApiKeyQuery()),
      queryClient.ensureQueryData(corridorRelayQuery()),
    ]),
  component: ErpPage,
})

function ErpPage(): React.ReactElement {
  return (
    <PageContent>
      <PageTitle title="ERP" />
      <CorridorConnection />
      <Outlet />
    </PageContent>
  )
}

function CorridorConnection(): React.ReactElement {
  return (
    <section className="space-y-2">
      <SectionHeader title="Corridor" icon={<CorridorMark />} />
      <Card className="overflow-hidden shadow-none">
        <CardContent className="divide-y p-0">
          <ApiKeyRow />
          <RelayRow />
        </CardContent>
      </Card>
    </section>
  )
}

function CorridorMark(): React.ReactElement {
  return (
    <img
      src="/assets/images/corridor-mark.png"
      alt=""
      className="size-5 rounded"
    />
  )
}

function ApiKeyRow(): React.ReactElement {
  const {
    data: { api_key: apiKey },
  } = useSuspenseQuery(erpApiKeyQuery())
  const canUpdate = useHasPermission("erp_config:update")

  return (
    <IntegrationRow
      title="API key"
      description={
        apiKey
          ? `${apiKey.key_prefix}… · set ${formatShortDate(apiKey.set_at)}`
          : "The Corridor sync sends this key in the X-API-Key header to import into this workspace"
      }
    >
      {canUpdate && (
        <div className="flex gap-2">
          {apiKey && (
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/integrations/erp/api-key/revoke">Revoke</Link>
            </Button>
          )}
          <Button asChild variant={apiKey ? "secondary" : "primary"} size="sm">
            <Link to="/settings/integrations/erp/api-key/set">
              {apiKey ? "Regenerate" : "Generate API key"}
            </Link>
          </Button>
        </div>
      )}
    </IntegrationRow>
  )
}

function RelayRow(): React.ReactElement {
  const {
    data: { relay },
  } = useSuspenseQuery(corridorRelayQuery())
  const canUpdate = useHasPermission("erp_config:update")

  return (
    <IntegrationRow
      title="Desktop relay"
      description={
        relay
          ? `${relay.key_prefix}… · set ${formatShortDate(relay.set_at)}`
          : "Sends quotes to the Corridor desktop through the relay's MCP key"
      }
    >
      {canUpdate && (
        <div className="flex gap-2">
          {relay && (
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/integrations/erp/relay/remove">Remove</Link>
            </Button>
          )}
          <Button asChild variant={relay ? "secondary" : "primary"} size="sm">
            <Link to="/settings/integrations/erp/relay/set">
              {relay ? "Change" : "Set up relay"}
            </Link>
          </Button>
        </div>
      )}
    </IntegrationRow>
  )
}
